import React, { FC, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import ColorWrapper from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import Svg from '@styles/icons/svg';
import { font as initialFont } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import { mapDocumentTypeSvg } from '@v2/components/imageHandling/constants';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { DocType, DocumentCategories } from '@v2/components/imageHandling/types/types';
import { selectDocumentCategories } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { fetchDocumentCategories } from '@reducers/v2/imageHandlingV2/thunks';
import { useSelector } from 'react-redux';
import svgRegister from '@styles/icons/svg/svgRegister';
import i18n from 'i18n-js';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface DocTypeModalFrameProps {
  C: ColorFunction;
  onSelect: (documentCategory: DocumentCategories['items'][number]) => void;
  data: DocumentCategories['items'];
  selected: string;
  loading: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

interface DocTypeModalProps {
  selectedValue: DocType;
  change: (newDocType: DocType) => Promise<void>;
  onClose: () => void;
  C: ColorFunction;
}

const DocTypeModalFrame: FC<DocTypeModalFrameProps> = ({ C, onSelect, data, selected, loading, scale = initialScale, font = initialFont }) => {
  const defaultIcon = 'other';

  return (
    <View testID={TEST_IDS.documentsDocTypeModal.root}>
      {/* Content */}
      <View>
        {loading ? (
          <View style={{ width: '100%', height: scale(200), justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
            <SmallLoading />
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={[
              {
                paddingHorizontal: scale(15),
                paddingTop: scale(15),
                paddingBottom: scale(18),
              },
            ]}
          >
            <View style={{ flexDirection: 'column' }}>
              {data.map((item, index) => {
                return (
                  <TouchableElement
                    key={`${item.value}-${index}`}
                    onPress={() => {
                      onSelect(item);
                    }}
                    hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  >
                    {({ boxStyle, pressHoverColor }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            backgroundColor: C(pressHoverColor('Background/Light/Field on Panel')),
                          },
                          index === 0 && {
                            borderTopLeftRadius: scale(18),
                            borderTopRightRadius: scale(18),
                          },
                          index === data.length - 1 && {
                            borderBottomLeftRadius: scale(18),
                            borderBottomRightRadius: scale(18),
                          },
                          { overflow: 'visible', paddingTop: scale(index === 0 ? 3 : 0) },
                        ]}
                      >
                        <View style={{ height: scale(44) }}>
                          {/* Row: icon + text column + optional check */}
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              height: '100%',
                              zIndex: index,
                              paddingRight: scale(16),
                              paddingLeft: scale(1),
                            }}
                          >
                            {/* Document type icon */}
                            <View
                              style={{
                                width: scale(46),
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'center',
                              }}
                            >
                              <Svg
                                icon={(mapDocumentTypeSvg[item.name]?.icon || defaultIcon) as keyof typeof svgRegister}
                                color="Label/Light/Primary"
                                width={scale(mapDocumentTypeSvg[item.name]?.width || 14)}
                                height={scale(mapDocumentTypeSvg[item.name]?.height || 18)}
                              />
                            </View>

                            {/* Text column */}
                            <View
                              style={{
                                flex: 1,
                                marginLeft: scale(1),
                                justifyContent: 'center',
                              }}
                            >
                              <Text
                                style={[
                                  font.Body,
                                  {
                                    color: C(pressHoverColor('Label/Light/Primary')),
                                    paddingRight: scale(40),
                                  },
                                ]}
                              >
                                {i18n.t(item.name)}
                              </Text>
                            </View>

                            {/* Selected check icon */}
                            {item.value === selected && <Svg icon="check" color="Selected/Light/Primary" width={scale(15)} height={scale(15)} />}
                          </View>
                        </View>
                        {/* Divider line (starts after icon) */}
                        {index !== data.length - 1 && (
                          <View
                            style={{
                              height: StyleSheet.hairlineWidth,
                              marginLeft: scale(46),
                              backgroundColor: C(pressHoverColor('Hairline/Light/Transpar')),
                            }}
                          />
                        )}
                      </View>
                    )}
                  </TouchableElement>
                );
              })}
            </View>
          </ScrollView>
        )}
      </View>
    </View>
  );
};

const DocTypeModal: FC<DocTypeModalProps> = ({ selectedValue, change, onClose, C }) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selected, setSelected] = useState(selectedValue);

  const dispatch = useAppDispatch();
  const documentCategories = useSelector(selectDocumentCategories);

  useEffect(() => {
    if (!documentCategories.loaded) {
      dispatch(fetchDocumentCategories());
    }
  }, []);

  const onSelect = async (item: DocumentCategories['items'][number]) => {
    if (onClose) {
      onClose();
    }
    setSelected(item.value);
    setSubmitLoading(true);
    if (change) {
      await change(item.value);
    }
    setSubmitLoading(false);
  };

  return (
    <DocTypeModalFrame
      {...{
        submitLoading,
        data: documentCategories.items || [],
        onSelect,
        selected,
        loading: !documentCategories.loaded,
        C,
      }}
    />
  );
};

export default ColorWrapper(DocTypeModal);
